import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlowSchema } from '../components/FlowSchema';
import { LayersSchema } from '../components/LayersSchema';
import { MachineSchema } from '../components/MachineSchema';

type SchemaTab = 'flow' | 'layers' | 'machine';
const TAB_KEY = 'rv-schema-tab';

function readTab(): SchemaTab {
  try {
    const stored = localStorage.getItem(TAB_KEY);
    if (stored === 'flow' || stored === 'layers' || stored === 'machine') return stored;
  } catch {
    // ignore
  }
  return 'flow';
}

export function Overview() {
  const [tab, setTab] = useState<SchemaTab>(readTab);

  useEffect(() => {
    document.title = 'Revvly Docs — Subscription Revenue Management Reference';
  }, []);

  const setT = (next: SchemaTab) => {
    setTab(next);
    try {
      localStorage.setItem(TAB_KEY, next);
    } catch {
      // ignore
    }
  };

  return (
    <div className="tn-page-wide">
      <div className="tn-home-hero">
        <span className="rv-eyebrow-mini">Revvly · documentation</span>
        <h1>
          Orchestrate the flow of <em>revenue</em>.
        </h1>
        <p>
          An interactive reference for subscriptions, pricing, billing, and analytics inside
          Revvly. Click any node to jump to its concept page.
        </p>
        <p className="tn-home-scope">
          These docs cover the revenue core — catalog, pricing engine, subscriptions, usage
          ingestion, invoicing, and the analytics that derive from them — plus the CRM add-on that
          feeds new business into it, from prospect to signed customer. Dashboard-first surfaces
          (team management, onboarding, integrations settings) are documented in-app.
        </p>
        <div className="tn-home-chips">
          <Link className="primary" to="/docs/quickstart">
            Quickstart ›
          </Link>
          <Link to="/docs/example">Follow the Acme Corp example</Link>
          <Link to="/docs/concepts/subscription">Jump to Subscription</Link>
          <Link to="/docs/concepts/variableComponent">Jump to Variable Component</Link>
        </div>
      </div>
      <div className="tn-schema-tabs" role="tablist" aria-label="Schema view">
        <button
          id="tab-flow"
          role="tab"
          aria-selected={tab === 'flow'}
          aria-controls="panel-flow"
          className={`dh-tab ${tab === 'flow' ? 'dh-tab-active' : ''}`}
          onClick={() => setT('flow')}
        >
          01 · Flow
        </button>
        <button
          id="tab-layers"
          role="tab"
          aria-selected={tab === 'layers'}
          aria-controls="panel-layers"
          className={`dh-tab ${tab === 'layers' ? 'dh-tab-active' : ''}`}
          onClick={() => setT('layers')}
        >
          02 · Layers
        </button>
        <button
          id="tab-machine"
          role="tab"
          aria-selected={tab === 'machine'}
          aria-controls="panel-machine"
          className={`dh-tab ${tab === 'machine' ? 'dh-tab-active' : ''}`}
          onClick={() => setT('machine')}
        >
          03 · Machine
        </button>
      </div>
      <div className="tn-schema-body">
        <div id="panel-flow" role="tabpanel" aria-labelledby="tab-flow" hidden={tab !== 'flow'}>
          <FlowSchema />
        </div>
        <div
          id="panel-layers"
          role="tabpanel"
          aria-labelledby="tab-layers"
          hidden={tab !== 'layers'}
        >
          <LayersSchema />
        </div>
        <div
          id="panel-machine"
          role="tabpanel"
          aria-labelledby="tab-machine"
          hidden={tab !== 'machine'}
        >
          <MachineSchema />
        </div>
      </div>
    </div>
  );
}
